import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, MapPin, ShieldCheck, Loader2, ArrowRight } from "lucide-react";

export default function Reviews() {
  const [params, setParams] = useSearchParams();
  const area = params.get("area") || "";
  const service = params.get("service") || "";

  const [filters, setFilters] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 24;

  useEffect(() => {
    api.get("/testimonials/filters")
      .then((r) => setFilters(r.data))
      .catch(() => setFilters({ areas: [], services: [], rating_avg: 5, total: 0 }));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (area) qs.set("area", area);
    if (service) qs.set("service", service);
    qs.set("limit", String(LIMIT));
    qs.set("offset", "0");
    api.get(`/testimonials?${qs.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setOffset(r.data.items?.length || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [area, service]);

  const loadMore = async () => {
    const qs = new URLSearchParams();
    if (area) qs.set("area", area);
    if (service) qs.set("service", service);
    qs.set("limit", String(LIMIT));
    qs.set("offset", String(offset));
    const r = await api.get(`/testimonials?${qs.toString()}`);
    setItems([...items, ...(r.data.items || [])]);
    setOffset(offset + (r.data.items?.length || 0));
  };

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || next.get(k) === v) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const avg = filters?.rating_avg || 5;
  const totalAll = filters?.total || 0;

  return (
    <div data-testid="reviews-page" className="pt-32">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-10">
        <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Reviews</div>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl text-[#F0F4F1] max-w-4xl">
          <span className="text-[#8DE038]">Leeds locals.</span>
        </h1>
        {totalAll > 0 && (
          <div className="mt-6 flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-2">
              {Array.from({length:5}).map((_,i)=>(<Star key={i} size={20} fill={i < Math.round(avg) ? "#8DE038" : "transparent"} className={i < Math.round(avg) ? "text-[#8DE038]" : "text-[#1F3325]"}/>))}
              <span className="font-display uppercase tracking-widest text-[#F0F4F1] ml-2">{avg} / 5</span>
            </div>
            <div className="text-[#9CAEA3] text-sm">from <strong className="text-[#F0F4F1]">{totalAll}</strong> verified Leeds customers</div>
          </div>
        )}
      </section>

      {/* FILTER CHIPS */}
      {filters && (filters.areas.length > 0 || filters.services.length > 0) && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-6 space-y-5">
          {filters.services.length > 0 && (
            <div>
              <div className="font-display uppercase tracking-widest text-[10px] text-[#9CAEA3] mb-2">Filter by service</div>
              <div className="flex flex-wrap gap-2">
                {filters.services.map((s) => (
                  <Chip key={s.name} active={service === s.name} onClick={() => setFilter("service", s.name)} testId={`chip-service-${s.name.toLowerCase().replace(/\s+/g,'-')}`}>
                    {s.name} <Count n={s.count} active={service === s.name}/>
                  </Chip>
                ))}
              </div>
            </div>
          )}
          {filters.areas.length > 0 && (
            <div>
              <div className="font-display uppercase tracking-widest text-[10px] text-[#9CAEA3] mb-2">Filter by Leeds area</div>
              <div className="flex flex-wrap gap-2">
                {filters.areas.map((a) => (
                  <Chip key={a.name} active={area === a.name} onClick={() => setFilter("area", a.name)} testId={`chip-area-${a.name.toLowerCase().replace(/\s+/g,'-')}`}>
                    <MapPin size={12}/> {a.name} <Count n={a.count} active={area === a.name}/>
                  </Chip>
                ))}
              </div>
            </div>
          )}
          {(area || service) && (
            <button data-testid="clear-filters" onClick={() => setParams({})} className="text-xs font-display uppercase tracking-widest text-[#8DE038]">Clear filters ×</button>
          )}
        </section>
      )}

      {/* REVIEWS GRID */}
      <section className="section-pad bg-[#050806] pt-6">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="text-[#9CAEA3] text-center py-20"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading…</div>
          ) : items.length === 0 ? (
            <div data-testid="reviews-empty" className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-16 text-center">No reviews match these filters.</div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((r) => (<ReviewCard key={r.id} r={r} />))}
              </div>
              {offset < total && (
                <div className="mt-10 text-center">
                  <button data-testid="load-more-reviews" onClick={loadMore} className="btn-secondary">Load more ({total - offset} remaining)</button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-20 text-center">
          <h3 className="font-display uppercase tracking-tighter text-3xl md:text-5xl text-[#F0F4F1]">Join {totalAll}+ happy customers</h3>
          <div className="mt-7"><Link to="/book" className="btn-primary">Book a Pro <ArrowRight size={18}/></Link></div>
        </div>
      </section>
    </div>
  );
}

function Chip({ active, children, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-2 border font-display uppercase tracking-widest text-[11px] transition-colors ${active ? "border-[#8DE038] bg-[#8DE038] text-[#050806]" : "border-[#1F3325] text-[#C8D4CB] hover:border-[#8DE038] hover:text-[#8DE038]"}`}>{children}</button>
  );
}
function Count({ n, active }) {
  return <span className={`text-[9px] px-1.5 py-0.5 ml-1 ${active ? "bg-[#050806] text-[#8DE038]" : "bg-[#0F1712] text-[#9CAEA3]"}`}>{n}</span>;
}

function ReviewCard({ r }) {
  return (
    <div data-testid={`review-${r.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 hover:border-[#8DE038] transition-colors flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {Array.from({length:5}).map((_,i)=>(<Star key={i} size={13} fill={i < r.rating ? "#8DE038" : "transparent"} className={i < r.rating ? "text-[#8DE038]" : "text-[#1F3325]"}/>))}
        </div>
        {r.verified && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#8DE038]"><ShieldCheck size={10}/> Verified</span>}
      </div>
      <p className="mt-3 text-[#C8D4CB] text-sm leading-relaxed flex-1">"{r.comment}"</p>
      <div className="mt-4 pt-3 border-t border-[#1F3325] flex items-center justify-between text-xs">
        <div>
          <div className="font-display uppercase tracking-widest text-[#F0F4F1]">{r.customer_name}</div>
          <div className="text-[#9CAEA3] mt-0.5 flex items-center gap-1 flex-wrap">
            {r.area && (<><MapPin size={10} className="text-[#8DE038]"/>{r.area}</>)}
            {r.area && r.service && <span className="text-[#1F3325]">·</span>}
            {r.service && <span>{r.service}</span>}
          </div>
        </div>
        {r.date && <div className="text-[10px] text-[#6B7C71] uppercase tracking-widest">{r.date}</div>}
      </div>
    </div>
  );
}
